import { useState } from 'react';
import { api, ago, humanise, type LiveTrip, type SosRow } from '../api';
import { Icon, Plate, Toast, useLoad } from '../ui';
import { FleetMap } from '../FleetMap';
import { TripMap } from '../TripMap';

const today = () => new Date().toISOString().slice(0, 10);
const LETTERS = 'ABCDEFGH';

/**
 * Mission control: what is running, what is wrong, and what needs a human.
 *
 * Numbers across the top, every live run on a map and in a table, and on the
 * right the things to act on — open SOS first, then riders without a driver,
 * documents waiting, and the maps budget. Polls: an SOS cannot wait for a
 * refresh someone forgot to press.
 */
export function Triage({ onGo }: { onGo: (tab: string) => void }) {
  const live = useLoad(() => api.live(), { pollMs: 10_000 });
  const sos = useLoad(() => api.sos(), { pollMs: 10_000 });
  const gaps = useLoad(() => api.supplyGaps(), { pollMs: 60_000 });
  const kyc = useLoad(() => api.kyc(), { pollMs: 60_000 });
  const budget = useLoad(() => api.budget(), { pollMs: 60_000 });
  const [filter, setFilter] = useState<'all' | 'running' | 'sos'>('all');
  const [q, setQ] = useState('');
  const [mapOf, setMapOf] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  const [toast, setToast] = useState<{ msg: string; bad?: boolean } | null>(null);

  const k = live.data?.kpis;
  const trips = live.data?.trips ?? [];
  const needle = q.trim().toLowerCase();
  const shown = trips.filter((t) =>
    (filter === 'all' || (filter === 'running' ? t.status === 'started' : t.sos)) &&
    (!needle || [t.vehicle?.plate, t.driver.name, t.from, t.to, t.id].some((f) => f?.toLowerCase().includes(needle))));
  const dark = document.documentElement.dataset.theme !== 'light';

  const plan = async (direction: 'morning' | 'evening') => {
    setPlanning(true);
    try {
      await api.runPlan({ serviceDate: today(), direction });
      setToast({ msg: `Planned today's ${direction} rides.` });
      void live.reload();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Planning failed', bad: true });
    } finally {
      setPlanning(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <>
      <div className="mc-head">
        <div>
          <h1><span className={`dot ${k?.openSos ? 'bad' : ''}`} /> Mission control</h1>
          <p>Every run today, live. Open SOS alerts come first, everywhere on this page.</p>
        </div>
        <div className="row">
          <button className="btn" onClick={() => onGo('trips')}><Icon name="table_view" size={17} /> All trips</button>
          <button className="btn primary" disabled={planning} onClick={() => void plan(new Date().getHours() < 12 ? 'morning' : 'evening')}>
            <Icon name="bolt" size={17} /> {planning ? 'Planning…' : `Run ${new Date().getHours() < 12 ? 'morning' : 'evening'} planner`}
          </button>
        </div>
      </div>

      <div className="kpis">
        <Kpi icon="directions_car" label="Running now" value={k ? String(k.activeRuns) : '—'} unit="trips" note={k ? `${k.scheduledRuns} more scheduled` : ''} bar={k && k.activeRuns + k.scheduledRuns ? k.activeRuns / (k.activeRuns + k.scheduledRuns) : 0} />
        <Kpi icon="groups" label="Riders in transit" value={k ? String(k.inTransit) : '—'} note={k && k.seatsTotal ? `${k.seatsInUse}/${k.seatsTotal} seats booked` : ''} />
        <Kpi icon="hub" label="Match rate" value={k?.matchRatePct != null ? `${k.matchRatePct}%` : '—'} tone="ok" note="riders with a ride" bar={k?.matchRatePct != null ? k.matchRatePct / 100 : 0} />
        <Kpi icon="timer" label="Avg curb wait" value={k?.avgCurbWaitSec != null ? `${Math.floor(k.avgCurbWaitSec / 60)}m ${k.avgCurbWaitSec % 60}s` : '—'} tone="amber" note="arrival to boarding, today" />
        <Kpi icon="emergency" label="Unresolved SOS" value={k ? String(k.openSos) : '—'} alarm={!!k?.openSos} note={k?.openSos ? 'Act now' : 'None open'} />
      </div>

      <div className="mc-grid">
        <div className="mc-main">
          <section className="panel">
            <div className="panel-head">
              <div className="chips">
                <button className={`chip ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>All today ({trips.length})</button>
                <button className={`chip ${filter === 'running' ? 'on' : ''}`} onClick={() => setFilter('running')}>Running ({k?.activeRuns ?? 0})</button>
                <button className={`chip ${filter === 'sos' ? 'on bad' : ''}`} onClick={() => setFilter('sos')}>SOS ({trips.filter((t) => t.sos).length})</button>
              </div>
            </div>
            <FleetMap trips={shown} dark={dark} onPick={(id) => setMapOf(id)} />
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2><Icon name="list_alt" size={18} /> Fleet today</h2>
              <div className="spacer" />
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Plate, driver, place, trip ID…" style={{ width: 230 }} />
            </div>
            {live.error ? <div className="empty">{live.error}</div> : shown.length === 0 ? (
              <div className="empty">{live.data ? 'No runs match.' : 'Loading…'}</div>
            ) : (
              <table className="fleet">
                <thead><tr><th>Trip</th><th>Vehicle & driver</th><th>Route</th><th>Riders</th><th>Status</th></tr></thead>
                <tbody>
                  {shown.map((t) => <FleetRow key={t.id} t={t} onMap={() => setMapOf(t.id)} />)}
                </tbody>
              </table>
            )}
          </section>
        </div>

        <aside className="mc-side">
          {/* The newest three in full; the rest as a list, so one bad morning
              does not push everything else off the screen. */}
          {(sos.data ?? []).slice(0, 3).map((r) => <SosCard key={r.id} r={r} trip={trips.find((t) => t.id === r.trip_id)} onResolved={() => { void sos.reload(); void live.reload(); }} />)}
          {(sos.data ?? []).length > 3 ? (
            <section className="sidecard" style={{ borderColor: 'var(--bad)' }}>
              <h3 style={{ color: 'var(--bad)' }}><Icon name="emergency" size={18} /> {(sos.data ?? []).length - 3} more open SOS</h3>
              {(sos.data ?? []).slice(3, 13).map((r) => (
                <div key={r.id} className="siderow">
                  <div><b>{r.full_name ?? 'Unknown'} · {humanise(r.role)}</b><small>{ago(r.created_at)}{r.notes ? ` · “${r.notes}”` : ''}</small></div>
                  {r.phone ? <a className="btn sm" href={`tel:${r.phone}`}><Icon name="call" size={14} /></a> : null}
                </div>
              ))}
              {(sos.data ?? []).length > 13 ? <p className="muted small">…and {(sos.data ?? []).length - 13} older.</p> : null}
            </section>
          ) : null}

          <section className="sidecard">
            <h3><Icon name="warning" size={18} /> Riders without a driver</h3>
            {(gaps.data ?? []).filter((g) => Number(g.unmatched) > 0).slice(0, 4).map((g) => (
              <div key={g.office ?? 'none'} className="siderow">
                <div><b>{g.office ?? 'Free drop-offs'}</b><small>{g.matched}/{g.riders} matched · {g.match_rate_pct ?? 0}%</small></div>
                <span className="pill warn">{g.unmatched} waiting</span>
              </div>
            ))}
            {(gaps.data ?? []).every((g) => Number(g.unmatched) === 0) ? <p className="muted small">Every rider with a route has a ride.</p> : null}
            <button className="link" onClick={() => onGo('supply')}>Full breakdown →</button>
          </section>

          <section className="sidecard">
            <h3><Icon name="fact_check" size={18} /> Documents waiting <span className="pill">{kyc.data?.length ?? 0}</span></h3>
            {(kyc.data ?? []).slice(0, 4).map((d) => (
              <div key={d.id} className="siderow">
                <div><b>{d.fullName ?? d.phone}</b><small>{humanise(d.requirement)} · {ago(d.createdAt)}</small></div>
                <button className="btn sm" onClick={() => onGo('kyc')}>Review</button>
              </div>
            ))}
            {kyc.data?.length === 0 ? <p className="muted small">Nothing to review.</p> : null}
          </section>

          {budget.data ? (
            <section className="sidecard">
              <h3><Icon name="map" size={18} /> Maps spend today</h3>
              <div className="bignum">₹{Math.round(budget.data.day_paise / 100)} <small>/ ₹{Math.round(budget.data.daily_cap_paise / 100)} a day</small></div>
              <div className="bar"><i style={{ width: `${Math.min(100, (budget.data.day_paise / Math.max(1, budget.data.daily_cap_paise)) * 100)}%` }} /></div>
              <p className="muted small">This month ₹{Math.round(budget.data.month_paise / 100)} of ₹{Math.round(budget.data.monthly_cap_paise / 100)}{budget.data.enforced ? ' · cap enforced' : ''}</p>
              <button className="link" onClick={() => onGo('checks')}>Maps checks →</button>
            </section>
          ) : null}
        </aside>
      </div>

      {mapOf ? <TripMap tripId={mapOf} onClose={() => setMapOf(null)} /> : null}
      {toast ? <Toast msg={toast.msg} bad={toast.bad} /> : null}
    </>
  );
}

function Kpi({ icon, label, value, unit, note, tone, alarm, bar }: {
  icon: string; label: string; value: string; unit?: string; note?: string; tone?: 'ok' | 'amber'; alarm?: boolean; bar?: number;
}) {
  return (
    <div className={`kpi ${alarm ? 'alarm' : ''}`}>
      <div className="kpi-top"><span>{label}</span><Icon name={icon} size={20} /></div>
      <div className={`kpi-value ${tone ?? ''}`}>{value}{unit ? <small> {unit}</small> : null}</div>
      {note ? <div className="kpi-note">{note}</div> : null}
      {bar != null && bar > 0 ? <div className="bar"><i style={{ width: `${Math.round(bar * 100)}%` }} /></div> : null}
    </div>
  );
}

function FleetRow({ t, onMap }: { t: LiveTrip; onMap: () => void }) {
  const total = t.legs.length;
  const status = t.sos ? { cls: 'bad', label: 'SOS' }
    : t.status === 'scheduled' ? { cls: '', label: 'Scheduled' }
    : t.dropped === total && total ? { cls: 'ok', label: 'All dropped' }
    : { cls: 'ok', label: t.position ? `Live · ${ago(new Date(t.position.at).toISOString())}` : 'Running' };
  return (
    <tr className={t.sos ? 'sosrow' : undefined} onClick={onMap}>
      <td className="shrink">
        <b className="mono">#{t.id.slice(0, 6).toUpperCase()}</b>
        <small className="detail">{humanise(t.direction)}{t.startedAt ? ` · ${new Date(t.startedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}</small>
      </td>
      <td>
        {t.vehicle ? <Plate number={t.vehicle.plate} /> : null}
        <small className="detail">{t.driver.name ?? '—'}{t.vehicle?.model ? ` · ${t.vehicle.model}` : ''}</small>
      </td>
      <td><small>{t.from?.split(',')[0] ?? '—'} → {t.to?.split(',')[0] ?? '—'}</small></td>
      <td className="shrink">
        <div className="seats">
          {t.legs.map((l, i) => (
            <span key={l.seq} className={`seat ${l.status === 'picked_up' ? 'on' : l.status === 'dropped' ? 'done' : l.status === 'skipped' ? 'miss' : ''}`} title={`${l.name ?? 'Rider'} — ${humanise(l.status)}`}>
              {LETTERS[i] ?? i + 1}
            </span>
          ))}
          <small>{t.boarded}/{total}</small>
        </div>
      </td>
      <td className="shrink"><span className={`state ${status.cls}`}>{status.label}</span></td>
    </tr>
  );
}

function SosCard({ r, trip, onResolved }: { r: SosRow; trip?: LiveTrip; onResolved: () => void }) {
  const [busy, setBusy] = useState(false);
  const resolve = async () => {
    const notes = window.prompt('What happened? (kept in the audit log)') ?? '';
    setBusy(true);
    try {
      await api.resolveSos(r.id, notes.trim() || undefined);
      onResolved();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not resolve that alert');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="soscard" role="alert">
      <div className="sos-head">
        <Icon name="emergency" size={22} fill />
        <b>SOS — {humanise(r.role)}</b>
        <span className="since">{ago(r.created_at)}</span>
      </div>
      <div className="sos-body">
        <div className="sos-who">
          <span className="avatar-sm">{(r.full_name ?? '?').slice(0, 1)}</span>
          <div><b>{r.full_name ?? 'Unknown'}</b>{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : null}</div>
        </div>
        {r.notes ? <p className="sos-note">“{r.notes}”</p> : null}
        {r.lat != null && r.lng != null ? (
          <a className="sos-loc" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${r.lat},${r.lng}`}>
            <Icon name="location_on" size={16} /> {r.lat.toFixed(5)}, {r.lng.toFixed(5)}
          </a>
        ) : null}
        {trip ? (
          <div className="sos-trip">
            {trip.vehicle ? <Plate number={trip.vehicle.plate} /> : null}
            <small>{trip.driver.name}{trip.vehicle?.model ? ` · ${trip.vehicle.model}` : ''}</small>
          </div>
        ) : null}
        <div className="sos-actions">
          {trip?.driver.phone ? <a className="btn sm" href={`tel:${trip.driver.phone}`}><Icon name="call" size={15} /> Call driver</a> : null}
          {r.phone ? <a className="btn sm" href={`tel:${r.phone}`}><Icon name="call" size={15} /> Call {humanise(r.role).toLowerCase()}</a> : null}
        </div>
        <a className="btn danger block" href="tel:112"><Icon name="local_police" size={17} /> Call 112</a>
        <button className="link" disabled={busy} onClick={() => void resolve()}>Resolve after the call →</button>
      </div>
    </section>
  );
}
